import {
  AfterViewInit,
  Component,
  EventEmitter,
  OnDestroy,
  OnInit,
  viewChild,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';
import { SelectItem } from '@openng/optimus-ui/api';
import { forkJoin, interval, Subscription } from 'rxjs';
import { take } from 'rxjs/operators';
import { OPLOG_DETAILS_CONTAINER } from 'src/app/constants/common.constants';
import {
  LazyLoadFilters,
  PageRequest,
  SearchContainerComponent,
} from 'src/app/core/containers/search-container/search-container.component';
import { TabService } from 'src/app/core/services/tab.service';
import { AuthApiService } from 'src/app/services/api/auth-api.service';
import { OpAlertLogTableService } from 'src/app/services/table/op-alert-log-table.service';
import { BreadCrumbBuilderService } from 'src/app/services/builder/breadcrumb-builder.service';
import { BusinessUnitFilterComponent } from 'src/app/core/components/business-unit-filter/business-unit-filter.component';
import { OperationalMonitoringApiService } from 'src/app/services/api/operational-monitoring-api.service';
import {
  OpAlertLog,
  OpAlertLogSortOrQuery,
} from 'src/app/model/opAlertLogs/op-alert-log';
import { OpAlertLogsBuilderService } from 'src/app/services/builder/op-alert-logs-builder.service';
import { getOpLogColumns } from '../../definitions/all-op-log-table-definition';
import { GridLazyLoadEvent } from 'src/app/core/components/base-grid.component';
import { BlockUI } from '@openng/optimus-ui/blockui';
import { ButtonDirective } from '@openng/optimus-ui/button';
import { Panel } from '@openng/optimus-ui/panel';
import { OpLogMobileFilterComponent } from '../../components/op-log-mobile-filter/op-log-mobile-filter.component';
import { ProgressSpinnerComponent } from '../../../core/components/progress-spinner.component';
import { GeneralGridComponent } from '../../../core/components/general-grid.component';
import { LogAndMessageService } from 'src/app/services/log-and-message.service';

@Component({
  selector: 'app-oplogs-all-logs-search-container',
  templateUrl: './oplogs-all-logs-search-container.component.html',
  styleUrls: ['./oplogs-all-logs-search-container.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    BlockUI,
    BusinessUnitFilterComponent,
    ButtonDirective,
    Panel,
    OpLogMobileFilterComponent,
    ProgressSpinnerComponent,
    GeneralGridComponent,
    TranslatePipe,
  ],
})
export class OplogsAllLogsSearchContainerComponent
  extends SearchContainerComponent<OpAlertLog>
  implements OnInit, OnDestroy, AfterViewInit
{
  protected _translateService = inject(TranslateService);
  protected _breadCrumbBuilderService = inject(BreadCrumbBuilderService);
  protected _opMonitoringApi = inject(OperationalMonitoringApiService);
  protected _opAlertLogBuilder = inject(OpAlertLogsBuilderService);
  protected _authApi = inject(AuthApiService);
  protected _tableService = inject(OpAlertLogTableService);
  protected _tabService = inject(TabService);
  protected _logAndMessage = inject(LogAndMessageService);

  readonly businessUnitFilter =
    viewChild<BusinessUnitFilterComponent>('buFilter');
  allLogs: any[] = [];
  activeLogTypes: any[] = [];
  logsLoading: boolean = false;
  availableCompanies: any[] = [];
  logsLoadingEvent = new EventEmitter<any>();
  additionalFilters = [];
  selectedAction: any;
  statusOptions: SelectItem[] = [];
  mobileFilterCollapsed = true;
  exportFilename: string = '';
  selectMessage: string = '';
  private _isRefreshing = false;
  refreshSubscription?: Subscription;

  constructor() {
    super();
  }

  ngOnInit() {
    this.setTableService(this._tableService);
    this.initialize();

    const refreshInterval = interval(60000);
    this.refreshSubscription = refreshInterval.subscribe(() => {
      this.refresh(false);
    });
  }

  ngAfterViewInit(): void {
    this.applyPreviousFilters();
    this.applyPreviousData();
  }

  initialize() {
    this.statusOptions = this._tableService.getStatusOptions();
    this.columns = getOpLogColumns(this.statusOptions, this.isMobileDevice());

    const breadCrumb$ = this._translateService.get(
      'OPMONITORING.SCREEN.ALL_OPERATIONAL_LOGS'
    );
    const header$ = this._translateService.get(
      'OPMONITORING.SCREEN.ALL_OPERATIONAL_LOGS'
    );
    const exportFilename$ = this._translateService.get(
      'MONLOG.EXPORT_FILE_NAME.ALL_LOGS'
    );
    forkJoin([header$, exportFilename$, breadCrumb$])
      .pipe(take(1))
      .subscribe((mes) => {
        if (this._deviceService.isMobile()) {
          this._breadCrumbBuilderService.resetAndAddBreadCrumb({
            label: mes[2],
            routerLink: null,
          });
        } else {
          this._tabService.updateActiveTabLabel(mes[0]);
        }
        this.screenName = mes[0];
        this.exportFilename = mes[1];
      });
  }

  ngOnDestroy(): void {
    this.refreshSubscription?.unsubscribe();
  }

  /**
   * Method called when a row double click event is fired.  This navigates to the details page.
   * @param $event - Event from the double click event.
   */
  changeViewEvent($event: { data: OpAlertLog }) {
    this._tableService.setSelected($event.data);
    this._tableService.setLastScreen('all');

    const tab = this._tabService.buildNewTab(
      OPLOG_DETAILS_CONTAINER,
      true,
      undefined,
      $event.data.id
    );
    if (this._deviceService.isMobile()) {
      this._tabService.setMobileTab(tab);
    } else {
      this._tabService.openTab(tab);
    }
  }

  override handleLazyLoad(
    req: PageRequest,
    $event: GridLazyLoadEvent,
    filters: LazyLoadFilters
  ) {
    let selectedBusinessUnits = [];
    const businessUnitFilter = this.businessUnitFilter();
    if (businessUnitFilter?.selectedBusinessUnits) {
      // Handle further refreshes & changes to business unit filter component
      selectedBusinessUnits = businessUnitFilter.selectedBusinessUnits;
    } else {
      // Handle initial page load (before the business unit filter subcomponent exists)
      selectedBusinessUnits = this._authApi
        .getUserBusinessUnits()
        .map((bu) => bu.id);
    }
    filters['businessUnits'] = selectedBusinessUnits as any;

    const query = this._opAlertLogBuilder.buildOpLogQuery(filters);
    this.queryNetwork(req, $event, query);
  }

  private queryNetwork(
    req: PageRequest,
    $event: GridLazyLoadEvent,
    query: { [key: string]: any }
  ) {
    req.page += 1;
    const sort: any = {};
    if ($event.lazy && $event.lazy.sortField) {
      // sorting
      if ($event.lazy.sortField === 'statusName') {
        sort['logStatus'] =
          $event.lazy.sortOrder && $event.lazy.sortOrder === 1 ? 'ASC' : 'DESC';
      } else {
        sort[$event.lazy.sortField as string] =
          $event.lazy.sortOrder && $event.lazy.sortOrder === 1 ? 'ASC' : 'DESC';
      }

      this._tableService.setLastSortField($event.lazy.sortField as string);
      this._tableService.setLastSortDirection($event.lazy.sortOrder as number);
    } else {
      sort['createdAt'] = 'DESC';
      this._tableService.setLastSortField('createdAt');
      this._tableService.setLastSortDirection(0);
    }

    if (!this._isRefreshing) {
      this._isRefreshing = true;
      this._opMonitoringApi
        .queryForOpAlertLogs(
          req.pageSize,
          req.page,
          query as OpAlertLogSortOrQuery,
          sort
        )
        .subscribe({
          next: (connection) => {
            this.totalRecords = connection.totalRecords;
            this.allLogs = [...connection.items];
            this.elements = [...this.allLogs];
            this.loading = false;
            this._isRefreshing = false;
          },
          error: (error) => {
            console.error(error);
            this._logAndMessage.translateToErrorAlert({
              headerKey: 'COMMON.MESSAGES.HEADERS.ERROR',
              bodyKey: 'MONLOG.MESSAGES.ERROR.LOAD_LOGS',
            });
            this.loading = false;
            this._isRefreshing = false;
          },
        });
    }
  }

  handleBusinessUnitChange() {
    this.lazyLoad({ lazy: this._tableService.getLastLazyLoad()! });
  }

  mobileFilter($event: any) {
    const newLazy: any = {
      filters: {
        ...this.grid()?.table()?.filters,
        logId: $event.logId
          ? {
              value: $event.logId,
              matchMode: 'contains',
            }
          : null,
        facilityName: $event.facilityName
          ? {
              value: $event.facilityName,
              matchMode: 'contains',
            }
          : null,
        logStatus: $event.statusName
          ? {
              value: $event.statusName,
              matchMode: 'contains',
            }
          : null,
      },
    };
    this.mobileFilterCollapsed = true;
    this._tableService.setLastLazyLoad(newLazy);
    this.lazyLoad({ lazy: this._tableService.getLastLazyLoad()! });
  }

  refresh(setLoading = true): void {
    this.lazyLoad({ lazy: this._tableService.getLastLazyLoad()! }, setLoading);
  }
}
